import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2, Save, X, Users } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Fornitori = () => {
  const [fornitori, setFornitori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ nome: '' });

  useEffect(() => {
    fetchFornitori();
  }, []);

  const fetchFornitori = async () => {
    try {
      const response = await axios.get(`${API}/fornitori`);
      setFornitori(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei fornitori');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/fornitori`, formData);
      toast.success('Fornitore creato con successo');
      fetchFornitori();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  const handleDelete = async (nome) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo fornitore?')) return;
    try {
      await axios.delete(`${API}/fornitori/${encodeURIComponent(nome)}`);
      toast.success('Fornitore eliminato con successo');
      fetchFornitori();
    } catch (error) {
      toast.error('Errore durante eliminazione');
    }
  };

  const resetForm = () => {
    setFormData({ nome: '' });
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="loading-indicator">
        <div className="w-8 h-8 border-4 border-[#FFE500] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="fornitori-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="bg-[#FFE500] p-2 rounded-xl">
            <Users className="w-5 h-5 text-[#0A1628]" />
          </div>
          <h2 className="text-2xl font-bold text-white uppercase tracking-tight">Gestione Fornitori</h2>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold uppercase tracking-wide text-xs transition-colors ${
            showForm 
              ? 'bg-white/10 text-white border border-white/20 hover:bg-white/20' 
              : 'bg-[#FFE500] text-[#0A1628] shadow-lg shadow-[#FFE500]/20 hover:bg-[#FFE500]/90'
          }`}
          data-testid="add-fornitore-btn"
        >
          {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showForm ? 'Annulla' : 'Nuovo Fornitore'}
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-5" data-testid="fornitore-form">
          <h3 className="text-lg font-bold text-white uppercase tracking-tight mb-4">Nuovo Fornitore</h3>
          <form onSubmit={handleSubmit} className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Nome Fornitore</label>
              <input
                type="text"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                required
                placeholder="es. ANDREA PAD IT"
                data-testid="fornitore-nome-input"
              />
            </div>
            <button
              type="submit"
              className="flex items-center gap-2 px-4 py-2 bg-[#FFE500] text-[#0A1628] rounded-xl font-bold uppercase tracking-wide text-xs shadow-lg shadow-[#FFE500]/20 hover:bg-[#FFE500]/90 transition-colors"
              data-testid="save-fornitore-btn"
            >
              <Save className="w-4 h-4" />
              Salva
            </button>
          </form>
        </div>
      )}

      {/* Tabella */}
      <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl overflow-hidden" data-testid="fornitori-table-container">
        <table className="text-sm w-full border-collapse" data-testid="fornitori-table">
          <thead>
            <tr className="bg-[#FFE500]/10">
              <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-4 text-left">Nome Fornitore</th>
              <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-4 text-left w-24">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {fornitori.length === 0 ? (
              <tr>
                <td colSpan="2" className="text-center py-8 text-white/50" data-testid="no-fornitori-message">
                  Nessun fornitore presente. Crea il primo fornitore.
                </td>
              </tr>
            ) : (
              fornitori.map((fornitore, index) => (
                <tr key={fornitore.nome} className="border-b border-white/10 hover:bg-white/5 transition-colors" data-testid={`fornitore-row-${index}`}>
                  <td className="py-3 px-4 font-medium text-white">{fornitore.nome}</td>
                  <td className="py-3 px-4">
                    <button
                      onClick={() => handleDelete(fornitore.nome)}
                      className="p-1.5 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                      data-testid={`delete-fornitore-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Fornitori;
